import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, Lock, User, Eye, EyeOff, UserPlus, AlertCircle, Loader2, CheckCircle } from 'lucide-react'
import { authService, RegisterCredentials } from '@/services/auth'
import { analytics, trackEvent } from '@/services/analytics'

export default function RegisterPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [formData, setFormData] = useState<RegisterCredentials>({
    email: '',
    password: '',
    username: ''
  })
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (name === 'confirmPassword') {
      setConfirmPassword(value)
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
    // Clear error when user starts typing
    if (error) setError(null)
  }

  const validateForm = (): string | null => {
    if (formData.username.length < 3) {
      return t('auth.errors.usernameTooShort')
    }
    if (formData.password.length < 6) {
      return t('auth.errors.passwordTooShort')
    }
    if (formData.password !== confirmPassword) {
      return t('auth.errors.passwordMismatch')
    }
    if (!formData.email.includes('@')) {
      return t('auth.errors.invalidEmail')
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    // PostHog: kayit formu submit denemesi (client tarafinda validasyon gecti)
    trackEvent({
      event: 'scraible_register_submit',
      properties: {
        page: 'register',
        method: 'email_password',
      },
    })

    setIsLoading(true)
    setError(null)

    try {
      const user = await authService.signUp(formData)

      // Kullanici olustuktan sonra distinct_id olarak Supabase user.id'yi sakla
      analytics.setDistinctId(user.id)
      trackEvent({
        event: 'scraible_register_success',
        distinct_id: user.id,
        properties: {
          page: 'register',
          method: 'email_password',
          email: user.email,
        },
      })

      try { sessionStorage.setItem('welcome_message', t('auth.welcome')) } catch {}
      navigate('/')
    } catch (err) {
      trackEvent({
        event: 'scraible_register_error',
        properties: {
          page: 'register',
          method: 'email_password',
        },
      })
      setError(err instanceof Error ? err.message : t('auth.errors.register'))
    } finally {
      setIsLoading(false)
    }
  }

  

  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    if (password.length === 0) return { strength: 0, label: '', color: '' }
    if (password.length < 6) return { strength: 1, label: t('auth.passwordWeak'), color: 'text-red-400' }
    if (password.length < 8) return { strength: 2, label: t('auth.passwordMedium'), color: 'text-yellow-400' }
    if (password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password)) {
      return { strength: 3, label: t('auth.passwordStrong'), color: 'text-green-400' }
    }
    return { strength: 2, label: t('auth.passwordMedium'), color: 'text-yellow-400' }
  }

  const passwordStrength = getPasswordStrength(formData.password)

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-2">
            {t('auth.registerTitle')}
          </h1>
          <p className="text-white/60 text-lg">
            {t('auth.registerSubtitle')}
          </p>
        </motion.div>

        {/* Register Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-6 md:p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="flex items-center space-x-3 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-300"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </motion.div>
            )}

            {/* Username Field */}
            <div className="space-y-2">
              <label className="block text-white/80 text-sm font-medium">
                {t('auth.username')}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="input-field pl-11"
                  placeholder={t('auth.usernamePlaceholder') as string}
                  required
                  disabled={isLoading}
                  minLength={3}
                />
              </div>
              {formData.username && formData.username.length >= 3 && (
                <div className="flex items-center space-x-2 text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  <span>{t('auth.usernameOk')}</span>
                </div>
              )}
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <label className="block text-white/80 text-sm font-medium">
                {t('auth.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="input-field pl-11"
                  placeholder={t('auth.emailPlaceholder') as string}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label className="block text-white/80 text-sm font-medium">
                {t('auth.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="input-field pl-11 pr-11"
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {/* Password Strength Indicator */}
              {formData.password && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/60">{t('auth.passwordStrength')}</span>
                    <span className={`text-sm font-medium ${passwordStrength.color}`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        passwordStrength.strength === 1 ? 'bg-red-500 w-1/3' :
                        passwordStrength.strength === 2 ? 'bg-yellow-500 w-2/3' :
                        passwordStrength.strength === 3 ? 'bg-green-500 w-full' : 'w-0'
                      }`}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <label className="block text-white/80 text-sm font-medium">
                {t('auth.confirmPassword')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={handleInputChange}
                  className="input-field pl-11 pr-11"
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                  disabled={isLoading}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {confirmPassword && formData.password && (
                <div className={`flex items-center space-x-2 text-sm ${
                  formData.password === confirmPassword ? 'text-green-400' : 'text-red-400'
                }`}>
                  {formData.password === confirmPassword ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>{t('auth.passwordsMatch')}</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4" />
                      <span>{t('auth.passwordsDontMatch')}</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t('auth.registering')}</span>
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5" />
                  <span>{t('auth.register')}</span>
                </>
              )}
            </button>
          </form>

          

          {/* Login Link */}
          <div className="text-center mt-6">
            <p className="text-white/60 text-sm">
              {t('auth.haveAccount')} {' '}
              <Link
                to="/login"
                className="text-gradient hover:text-gradient-gold transition-colors font-medium"
              >
                {t('auth.signIn')}
              </Link>
            </p>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-8"
        >
          <Link
            to="/"
            className="text-white/40 hover:text-white/60 transition-colors text-sm"
          >
            ← {t('auth.backHome')}
          </Link>
        </motion.div>
      </div>
    </div>
  )
}

